import React, { useState, useEffect, useRef } from 'react';
import { useDevice } from '../../context/DeviceContext';
import { downloadProtocolLog } from '../../utils/ProtocolLog';
import { decodeButtonTypeByte } from '../../utils/ButtonPerformance';
import {
  extractLodFromSensorFlag,
  formatLodDisplay,
  getSensorTypeName,
} from '../../utils/SensorLod';
import './DeviceDebugger.css';

const DeviceDebugger = ({ onClose }) => {
  const { messageHandler, isConnected, dataStore } = useDevice();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('messages'); // 'messages' or 'data'
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [informationData, setInformationData] = useState(null);
  const [mouseButtonData, setMouseButtonData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [colorData, setColorData] = useState(null);
  const [macroData, setMacroData] = useState(null);
  
  // 拖拽相关状态
  const [position, setPosition] = useState({ x: window.innerWidth - 540, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const debuggerRef = useRef(null);

  // 监听所有消息。不能以 isConnected 作为开关：连接流程（协议版本、
  // 配置、宏读取）全部发生在 isConnected 置 true 之前，正是排查问题
  // 最需要日志的时段。
  useEffect(() => {
    if (!messageHandler) return;

    const handleMessage = (data) => {
      setMessages(prev => {
        const newMessages = [...prev, {
          ...data,
          id: Date.now() + Math.random()
        }];
        return newMessages.slice(-100); // 保留最近100条
      });
    };

    messageHandler.on('message', handleMessage);

    return () => {
      messageHandler.off('message', handleMessage);
    };
  }, [messageHandler]);

  // 定期更新统计信息
  useEffect(() => {
    if (!messageHandler) return;

    const updateStats = () => {
      setStats(messageHandler.getStats());
    };

    updateStats();
    const interval = setInterval(updateStats, 2000);

    return () => clearInterval(interval);
  }, [messageHandler]);

  // 监听设备数据更新
  useEffect(() => {
    if (!dataStore) return;

    const handleDeviceConfig = (data) => setDeviceConfig(data);
    const handleInformationData = (data) => setInformationData(data);
    const handleMouseButtonData = (data) => setMouseButtonData(data);
    const handlePerformanceData = (data) => setPerformanceData(data);
    const handleColorData = (data) => setColorData(data);
    const handleMacroData = (data) => setMacroData(data);

    // 注册监听器
    dataStore.on('deviceConfig', handleDeviceConfig);
    dataStore.on('informationData', handleInformationData);
    dataStore.on('mouseButtonData', handleMouseButtonData);
    dataStore.on('performanceData', handlePerformanceData);
    dataStore.on('colorData', handleColorData);
    dataStore.on('macroData', handleMacroData);

    // 初始加载已有数据
    const existingConfig = dataStore.getData('deviceConfig');
    const existingInfo = dataStore.getData('informationData');
    const existingMouseButton = dataStore.getData('mouseButtonData');
    const existingPerformance = dataStore.getData('performanceData');
    const existingColor = dataStore.getData('colorData');
    const existingMacro = dataStore.getData('macroData');
    
    if (existingConfig) setDeviceConfig(existingConfig);
    if (existingInfo) setInformationData(existingInfo);
    if (existingMouseButton) setMouseButtonData(existingMouseButton);
    if (existingPerformance) setPerformanceData(existingPerformance);
    if (existingColor) setColorData(existingColor);
    if (existingMacro) setMacroData(existingMacro);

    return () => {
      dataStore.off('deviceConfig', handleDeviceConfig);
      dataStore.off('informationData', handleInformationData);
      dataStore.off('mouseButtonData', handleMouseButtonData);
      dataStore.off('performanceData', handlePerformanceData);
      dataStore.off('colorData', handleColorData);
      dataStore.off('macroData', handleMacroData);
    };
  }, [dataStore]);

  const clearMessages = () => {
    setMessages([]);
    if (messageHandler) {
      messageHandler.clearBuffer();
    }
  };

  // 拖拽处理函数
  const handleMouseDown = (e) => {
    if (e.target.closest('.debugger-header') && !e.target.closest('button') && !e.target.closest('select')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // 限制在视口范围内
        const maxX = window.innerWidth - (debuggerRef.current?.offsetWidth || 500);
        const maxY = window.innerHeight - 100; // 至少留100px可见
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, position]);

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.parsed?.type === filter;
  });

  // 渲染完整的设备配置数据
  const renderDeviceConfigFull = () => {
    if (!deviceConfig || !deviceConfig.parsed) {
      return <div className="no-data">暂无设备配置数据</div>;
    }

    const parsed = deviceConfig.parsed;
    
    return (
      <div className="data-section">
        <h4>设备配置信息</h4>
        <div className="data-table">
          <div className="data-row">
            <span className="data-label">命令 (Command)</span>
            <span className="data-value">{parsed.command} (0x{parsed.command?.toString(16).padStart(2, '0').toUpperCase()})</span>
          </div>
          <div className="data-row">
            <span className="data-label">索引 (Index)</span>
            <span className="data-value">{parsed.index}</span>
          </div>
          <div className="data-row">
            <span className="data-label">长度 (Length)</span>
            <span className="data-value">{parsed.length}</span>
          </div>
          <div className="data-row">
            <span className="data-label">CMD</span>
            <span className="data-value">{parsed.cmd}</span>
          </div>
          <div className="data-row">
            <span className="data-label">产品ID (Product ID)</span>
            <span className="data-value">0x{parsed.productId?.toString(16).padStart(8, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">软件版本 (Software Version)</span>
            <span className="data-value">{parsed.softwareVersion} (0x{parsed.softwareVersionRaw?.toString(16).padStart(4, '0').toUpperCase()})</span>
          </div>
          <div className="data-row">
            <span className="data-label">MAC地址 (MAC[5])</span>
            <span className="data-value">{parsed.mac?.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(':')}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Flag</span>
            <span className="data-value">{parsed.flag} (0x{parsed.flag?.toString(16).padStart(2, '0').toUpperCase()})</span>
          </div>
          <div className="data-row">
            <span className="data-label">MCU2产品ID (MCU2 Product ID)</span>
            <span className="data-value">0x{parsed.mcu2ProductId?.toString(16).padStart(8, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">MCU2软件版本 (MCU2 Software Version)</span>
            <span className="data-value">{parsed.mcu2SoftwareVersion} (0x{parsed.mcu2SoftwareVersionRaw?.toString(16).padStart(4, '0').toUpperCase()})</span>
          </div>
          <div className="data-row">
            <span className="data-label">MCU2 MAC地址 (MCU2 MAC[5])</span>
            <span className="data-value">{parsed.mcu2Mac?.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(':')}</span>
          </div>
          <div className="data-row">
            <span className="data-label">MCU2 Flag</span>
            <span className="data-value">{parsed.mcu2Flag} (0x{parsed.mcu2Flag?.toString(16).padStart(2, '0').toUpperCase()})</span>
          </div>
          <div className="data-row">
            <span className="data-label">WirelessOnConnected</span>
            <span className="data-value">{parsed.wirelessOnConnected}</span>
          </div>
          <div className="data-row">
            <span className="data-label">设备ID (Device ID)</span>
            <span className="data-value">0x{parsed.deviceId?.toString(16).padStart(8, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">USB VID</span>
            <span className="data-value">0x{parsed.usbVID?.toString(16).padStart(4, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">USB PID</span>
            <span className="data-value">0x{parsed.usbPID?.toString(16).padStart(4, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">TFT MCU3版本 (TFT MCU3 Version)</span>
            <span className="data-value">{parsed.tftMcu3Version} (0x{parsed.tftMcu3VersionRaw?.toString(16).padStart(4, '0').toUpperCase()})</span>
          </div>
          <div className="data-row">
            <span className="data-label">MCU4版本 (MCU4 Version)</span>
            <span className="data-value">{parsed.mcu4Version} (0x{parsed.mcu4VersionRaw?.toString(16).padStart(4, '0').toUpperCase()})</span>
          </div>
        </div>
        <div className="data-timestamp">
          更新时间: {new Date(deviceConfig.timestamp).toLocaleString()}
        </div>
      </div>
    );
  };

  // 渲染鼠标按键数据
  const renderMouseButtonData = () => {
    if (!mouseButtonData || !mouseButtonData.parsed) {
      return <div className="no-data">暂无鼠标按键数据</div>;
    }

    const parsed = mouseButtonData.parsed;
    
    return (
      <div className="data-section">
        <h4>鼠标按键信息 (命令3)</h4>
        <div className="data-table">
          <div className="data-row">
            <span className="data-label">左键 (Left Button)</span>
            <span className="data-value">{parsed.leftButton}</span>
          </div>
          <div className="data-row">
            <span className="data-label">右键 (Right Button)</span>
            <span className="data-value">{parsed.rightButton}</span>
          </div>
          <div className="data-row">
            <span className="data-label">中键 (Middle Button)</span>
            <span className="data-value">{parsed.middleButton}</span>
          </div>
          <div className="data-row">
            <span className="data-label">侧键4 (Button 4)</span>
            <span className="data-value">{parsed.button4}</span>
          </div>
          <div className="data-row">
            <span className="data-label">侧键5 (Button 5)</span>
            <span className="data-value">{parsed.button5}</span>
          </div>
          <div className="data-row">
            <span className="data-label">DPI键 (DPI Button)</span>
            <span className="data-value">{parsed.dpiButton}</span>
          </div>
          <div className="data-row">
            <span className="data-label">回报率键 (Report Rate Button)</span>
            <span className="data-value">{parsed.reportRateButton}</span>
          </div>
          <div className="data-row">
            <span className="data-label">配对键 (Pair Button)</span>
            <span className="data-value">{parsed.pairButton}</span>
          </div>
          <div className="data-row">
            <span className="data-label">滚轮上 (Scroll Up)</span>
            <span className="data-value">{parsed.scrollUp}</span>
          </div>
          <div className="data-row">
            <span className="data-label">滚轮下 (Scroll Down)</span>
            <span className="data-value">{parsed.scrollDown}</span>
          </div>
        </div>
        <div className="data-timestamp">
          更新时间: {new Date(mouseButtonData.timestamp).toLocaleString()}
        </div>
      </div>
    );
  };

  // 渲染性能数据
  const renderPerformanceData = () => {
    if (!performanceData || (!performanceData.type1 && !performanceData.type3)) {
      return <div className="no-data">暂无性能数据</div>;
    }

    return (
      <>
        {performanceData.type1 && performanceData.type1.parsed && (
          <div className="data-section">
            <h4>性能数据 - 传感器设置 (命令4, 类型1)</h4>
            <div className="data-table">
              <div className="data-row">
                <span className="data-label">角度捕捉 (Angle Snapping)</span>
                <span className="data-value">{performanceData.type1.parsed.angleSnapEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">涟漪控制 (Ripple)</span>
                <span className="data-value">{performanceData.type1.parsed.rippleEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">运动同步 (Motion Sync)</span>
                <span className="data-value">{performanceData.type1.parsed.motionSyncEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">全速模式 (Full Speed Mode)</span>
                <span className="data-value">{performanceData.type1.parsed.fullSpeedEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">最大速度模式 (Max Speed Mode)</span>
                <span className="data-value">{performanceData.type1.parsed.fullbloodEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">抬起下降检测 (Lift Down Enable)</span>
                <span className="data-value">{performanceData.type1.parsed.liftoffButtonEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">玻璃模式 (Glass Mode)</span>
                <span className="data-value">{performanceData.type1.parsed.glassModeDiable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">抬起距离 (Lift Off Height)</span>
                <span className="data-value">
                  {formatLodDisplay(
                    informationData?.parsed?.sensorType,
                    performanceData.type1.parsed.liftoffHeight
                  )} (原始值: {performanceData.type1.parsed.liftoffHeight})
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">角度偏移 (Angle Tunability)</span>
                <span className="data-value">{performanceData.type1.parsed.angleTunability}°</span>
              </div>
              <div className="data-row">
                <span className="data-label">当前DPI档位 (DPI Current Level)</span>
                <span className="data-value">{performanceData.type1.parsed.dpiCurrentLevel}</span>
              </div>
              <div className="data-row">
                <span className="data-label">DPI标志 (DPI Flag)</span>
                <span className="data-value">{performanceData.type1.parsed.dpiflag}</span>
              </div>
              <div className="data-row">
                <span className="data-label">DPI表格（DPI Table）</span>
                <div>
                  {performanceData.type1.parsed.dpiTable.map((dpi, index) => (
                    <div key={index} style={{ marginBottom: '4px', fontFamily: 'monospace' }}>
                      <span className="data-value" style={{ display: 'inline-block', width: '80px' }}>X={dpi.x}</span>
                      <span className="data-value" style={{ display: 'inline-block', width: '80px' }}>Y={dpi.y}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="data-timestamp">
              更新时间: {new Date(performanceData.type1.timestamp).toLocaleString()}
            </div>
          </div>
        )}
        
        {performanceData.type3 && performanceData.type3.parsed && (
          <div className="data-section">
            <h4>性能数据 - 系统和按键设置 (命令4, 类型3)</h4>
            <div className="data-table">
              <div className="data-row">
                <span className="data-label">系统休眠时间 (System Sleep Time)</span>
                <span className="data-value">{performanceData.type3.parsed.SysSleepTime} s</span>
              </div>
              <div className="data-row">
                <span className="data-label">BLE休眠时间 (BLE System Sleep Time)</span>
                <span className="data-value">{performanceData.type3.parsed.bleSysSleepTime} s</span>
              </div>
              <div className="data-row">
                <span className="data-label">有线回报率 (coredReportRate)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.coredReportRateCustom
                    ? `自定义 ${performanceData.type3.parsed.coredReportRateHz} Hz`
                    : `${performanceData.type3.parsed.coredReportRateHz ?? ''} Hz / 档位 ${
                        performanceData.type3.parsed.coredReportRateLevel ??
                        performanceData.type3.parsed.coredReportRate
                      }`}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">无线回报率 (wirelessReportRate)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.wirelessReportRateCustom
                    ? `自定义 ${performanceData.type3.parsed.wirelessReportRateHz} Hz`
                    : `${performanceData.type3.parsed.wirelessReportRateHz ?? ''} Hz / 档位 ${
                        performanceData.type3.parsed.wirelessReportRateLevel ??
                        performanceData.type3.parsed.wirelessReportRate
                      }`}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">去抖时间 (Debounce Time)</span>
                <span className="data-value">{performanceData.type3.parsed.leftDebouncetimePress} ms</span>
              </div>
              <div className="data-row">
                <span className="data-label">快速响应 (Quick Response)</span>
                <span className="data-value">{performanceData.type3.parsed.fastButtonEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">按键唤醒 (Button Wakeup)</span>
                <span className="data-value">{performanceData.type3.parsed.buttonWakeupEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">移动唤醒 (Move Wakeup)</span>
                <span className="data-value">{performanceData.type3.parsed.moveWakeupEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">滚轮唤醒 (Wheel Wakeup)</span>
                <span className="data-value">{performanceData.type3.parsed.wheelWakeupEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">滚轮反向 (Wheel Reverse)</span>
                <span className="data-value">{performanceData.type3.parsed.wheelReverseEnable}</span>
              </div>
              <div className="data-row">
                <span className="data-label">按键类型 (Button Type 位域)</span>
                <span className="data-value">
                  {(() => {
                    const bits = decodeButtonTypeByte(
                      performanceData.type3.parsed.buttonType
                    );
                    return `${performanceData.type3.parsed.buttonType} (类型 ${bits.baseType} / 左模块 ${bits.leftModule} / 右模块 ${bits.rightModule})`;
                  })()}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">左键模式 (Left Button Mode)</span>
                <span className="data-value">{performanceData.type3.parsed.leftButtonMode}</span>
              </div>
              <div className="data-row">
                <span className="data-label">右键模式 (Right Button Mode)</span>
                <span className="data-value">{performanceData.type3.parsed.rightButtonMode}</span>
              </div>
              <div className="data-row">
                <span className="data-label">RF功率模式 (RF Power Mode)</span>
                <span className="data-value">{performanceData.type3.parsed.rfPowerMode}</span>
              </div>
              <div className="data-row">
                <span className="data-label">BLE设备数 (BLE Num)</span>
                <span className="data-value">{performanceData.type3.parsed.bleNum}</span>
              </div>
              <div className="data-row">
                <span className="data-label">左键防抖 按下/放开 (Left Debounce)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.leftDebouncetimePress} /{' '}
                  {performanceData.type3.parsed.leftDebouncetimeFree} ms
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">右键防抖 按下/放开 (Right Debounce)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.rightDebouncetimePress} /{' '}
                  {performanceData.type3.parsed.rightDebouncetimeFree} ms
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">其他按键防抖 按下/放开 (Other Debounce)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.otherDebouncetimePress} /{' '}
                  {performanceData.type3.parsed.otherDebouncetimeFree} ms
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">左键 RT 触碰点深度 按下/回弹 (Left RT)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.leftButtonRTPress} /{' '}
                  {performanceData.type3.parsed.leftButtonRTFree}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">右键 RT 触碰点深度 按下/回弹 (Right RT)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.rightButtonRTPress} /{' '}
                  {performanceData.type3.parsed.rightButtonRTFree}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">左键马达 类型/挡位 (Left Motor)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.leftMotorType} /{' '}
                  {performanceData.type3.parsed.leftMotorLevel}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">右键马达 类型/挡位 (Right Motor)</span>
                <span className="data-value">
                  {performanceData.type3.parsed.rightMotorType} /{' '}
                  {performanceData.type3.parsed.rightMotorLevel}
                </span>
              </div>
            </div>
            <div className="data-timestamp">
              更新时间: {new Date(performanceData.type3.timestamp).toLocaleString()}
            </div>
          </div>
        )}
      </>
    );
  };

  // 渲染颜色数据
  const renderColorData = () => {
    if (!colorData || !colorData.parsed) {
      return null; // 没有数据时不显示
    }

    const parsed = colorData.parsed;
    
    return (
      <div className="data-section">
        <h4>颜色数据 (命令5)</h4>
        <div className="data-table">
          <div className="data-row">
            <span className="data-label">效果休眠时间 (Effect Sleep Time)</span>
            <span className="data-value">{parsed.effectSleepTime} ms</span>
          </div>
          <div className="data-row">
            <span className="data-label">效果 (Effect)</span>
            <span className="data-value">{parsed.effect}</span>
          </div>
          <div className="data-row">
            <span className="data-label">原始数据长度</span>
            <span className="data-value">{parsed.rawData?.length || 0} 字节</span>
          </div>
        </div>
        <div className="data-timestamp">
          更新时间: {new Date(colorData.timestamp).toLocaleString()}
        </div>
      </div>
    );
  };

  // 渲染宏数据
  const renderMacroData = () => {
    if (!macroData || !macroData.parsed) {
      return null; // 没有数据时不显示
    }

    const parsed = macroData.parsed;
    
    // 将原始数据转为十六进制字符串
    const hexString = parsed.rawData
      ? parsed.rawData.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ')
      : '';

    return (
      <div className="data-section">
        <h4>宏数据 (命令7)</h4>
        <div className="data-table">
          <div className="data-row">
            <span className="data-label">命令</span>
            <span className="data-value">0x{parsed.command?.toString(16).padStart(2, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">类型</span>
            <span className="data-value">{parsed.type}</span>
          </div>
          <div className="data-row">
            <span className="data-label">数据长度字段</span>
            <span className="data-value">{parsed.dataLength} 字节</span>
          </div>
          <div className="data-row">
            <span className="data-label">宏对象数量</span>
            <span className="data-value" style={{ color: '#ff9800', fontWeight: 'bold' }}>{parsed.macroCount || 0} 个</span>
          </div>
          <div className="data-row">
            <span className="data-label">总字节数</span>
            <span className="data-value">{parsed.totalBytes} 字节</span>
          </div>
        </div>
        <div className="data-row" style={{ marginTop: '10px' }}>
          <span className="data-label">原始数据 (Hex)</span>
        </div>
        <div style={{
          fontFamily: 'monospace',
          fontSize: '11px',
          background: '#1a1a1a',
          padding: '8px',
          borderRadius: '4px',
          wordBreak: 'break-all',
          maxHeight: '150px',
          overflowY: 'auto',
          color: '#88ff88'
        }}>
          {hexString || '无数据'}
        </div>
        <div className="data-timestamp">
          更新时间: {new Date(macroData.timestamp).toLocaleString()}
        </div>
      </div>
    );
  };

  // 渲染通告信息数据（原始数值，不做解析）
  const renderInformationDataRaw = () => {
    if (!informationData || !informationData.parsed) {
      return <div className="no-data">暂无通告信息</div>;
    }

    const parsed = informationData.parsed;
    
    return (
      <div className="data-section">
        <h4>设备通告信息</h4>
        <div className="data-table">
          <div className="data-row">
            <span className="data-label">命令 (Command)</span>
            <span className="data-value">{parsed.command} (0x{parsed.command?.toString(16).padStart(2, '0').toUpperCase()})</span>
          </div>
          <div className="data-row">
            <span className="data-label">索引 (Index)</span>
            <span className="data-value">{parsed.index}</span>
          </div>
          <div className="data-row">
            <span className="data-label">长度 (Length)</span>
            <span className="data-value">{parsed.length}</span>
          </div>
          <div className="data-row">
            <span className="data-label">类型 (Type)</span>
            <span className="data-value">{parsed.type}</span>
          </div>
          <div className="data-row">
            <span className="data-label">设备状态 (Device State)</span>
            <span className="data-value">{parsed.deviceState}</span>
          </div>
          <div className="data-row">
            <span className="data-label">最大报告率 (Report Rate Max)</span>
            <span className="data-value">{parsed.reportRateMax}</span>
          </div>
          <div className="data-row">
            <span className="data-label">设备PID (Device PID)</span>
            <span className="data-value">0x{parsed.devicePID?.toString(16).padStart(4, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">设备VID (Device VID)</span>
            <span className="data-value">0x{parsed.deviceVID?.toString(16).padStart(4, '0').toUpperCase()}</span>
          </div>
          <div className="data-row">
            <span className="data-label">充电标志 (Charging Flag)</span>
            <span className="data-value">{parsed.chargingFlag}</span>
          </div>
          <div className="data-row">
            <span className="data-label">电量 (Battery Level)</span>
            <span className="data-value">{parsed.batteryLevel ?? parsed.batLevel}</span>
          </div>
          {parsed.updateFlag !== undefined && (
            <div className="data-row">
              <span className="data-label">更新标志 (Update Flag)</span>
              <span className="data-value">{parsed.updateFlag}</span>
            </div>
          )}
          <div className="data-row">
            <span className="data-label">配置序号 (Profile Index)</span>
            <span className="data-value">{parsed.profileIndex}</span>
          </div>
          {parsed.sensorType !== undefined && (
            <div className="data-row">
              <span className="data-label">传感器类型 (Sensor Type)</span>
              <span className="data-value">
                {parsed.sensorType} ({getSensorTypeName(parsed.sensorType)})
              </span>
            </div>
          )}
          {parsed.sensorNC !== undefined && (
            <div className="data-row">
              <span className="data-label">传感器 NC (Sensor NC)</span>
              <span className="data-value">{parsed.sensorNC}</span>
            </div>
          )}
          <div className="data-row">
            <span className="data-label">DPI序号 (DPI Index)</span>
            <span className="data-value">{parsed.dpiIndex}</span>
          </div>
          <div className="data-row">
            <span className="data-label">DPI X</span>
            <span className="data-value">{parsed.dpiX}</span>
          </div>
          <div className="data-row">
            <span className="data-label">DPI Y</span>
            <span className="data-value">{parsed.dpiY}</span>
          </div>
          <div className="data-row">
            <span className="data-label">传感器标志 (Sensor Flag)</span>
            <span className="data-value">{parsed.sensorFlag}</span>
          </div>
          {parsed.sensorFlag !== undefined && (
            <div className="data-row">
              <span className="data-label">LOD (sensorFlag Bit0-4)</span>
              <span className="data-value">
                {formatLodDisplay(
                  parsed.sensorType,
                  extractLodFromSensorFlag(parsed.sensorFlag)
                )} (原始值: {extractLodFromSensorFlag(parsed.sensorFlag)})
              </span>
            </div>
          )}
          {parsed.nc !== undefined && (
            <div className="data-row">
              <span className="data-label">NC</span>
              <span className="data-value">{parsed.nc}</span>
            </div>
          )}
          <div className="data-row">
            <span className="data-label">报告率 (Report Rate)</span>
            <span className="data-value">{parsed.reportRate}</span>
          </div>
          <div className="data-row">
            <span className="data-label">灯光模式 (Lighting Mode)</span>
            <span className="data-value">{parsed.lightingMode}</span>
          </div>
          <div className="data-row">
            <span className="data-label">灯光颜色 (Lighting Color)</span>
            <span className="data-value">{parsed.lightingColor}</span>
          </div>
          <div className="data-row">
            <span className="data-label">灯光速度 (Lighting Speed)</span>
            <span className="data-value">{parsed.lightingSpeed}</span>
          </div>
          <div className="data-row">
            <span className="data-label">灯光亮度 (Lighting Brightness)</span>
            <span className="data-value">{parsed.lightingBrightness}</span>
          </div>
          <div className="data-row">
            <span className="data-label">灯光方向 (Lighting Direction)</span>
            <span className="data-value">{parsed.lightingDirection}</span>
          </div>
          <div className="data-row">
            <span className="data-label">灯光淡入淡出 (Lighting Fade)</span>
            <span className="data-value">{parsed.lightingFade}</span>
          </div>
        </div>
        <div className="data-timestamp">
          更新时间: {new Date(informationData.timestamp).toLocaleString()}
        </div>
      </div>
    );
  };


  if (!isConnected) {
    return null;
  }

  return (
    <div 
      className={`device-debugger ${isDragging ? 'dragging' : ''}`}
      ref={debuggerRef}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`
      }}
    >
      <div 
        className="debugger-header"
        onMouseDown={handleMouseDown}
      >
        <div className="drag-handle">⋮⋮</div>
        <h3>设备调试器</h3>
        <div className="debugger-tabs">
          <button 
            className={`tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            消息日志
          </button>
          <button 
            className={`tab-btn ${activeTab === 'data' ? 'active' : ''}`}
            onClick={() => setActiveTab('data')}
          >
            设备数据
          </button>
        </div>
        {activeTab === 'messages' && (
          <>
            <button
              onClick={downloadProtocolLog}
              className="clear-btn"
              title="导出从页面加载起的完整收发时间线（不依赖控制台）"
            >
              导出协议日志
            </button>
            <button onClick={clearMessages} className="clear-btn">清空</button>
          </>
        )}
        <button onClick={onClose} className="close-btn" title="关闭调试器">×</button>
      </div>

      {activeTab === 'messages' ? (
        <>
          <div className="debugger-stats">
            <div className="stat-item">
              <span className="stat-label">总消息数:</span>
              <span className="stat-value">{stats?.totalMessages || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">当前显示:</span>
              <span className="stat-value">{filteredMessages.length}</span>
            </div>
          </div>

          <div className="debugger-filter">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">所有消息</option>
              <option value="mouse_input">鼠标输入</option>
              <option value="keyboard_input">键盘输入</option>
              <option value="config_response">配置响应</option>
              <option value="status_update">状态更新</option>
              <option value="raw_data">原始数据</option>
            </select>
          </div>

          <div className="debugger-messages">
            {filteredMessages.map((msg) => (
              <div key={msg.id} className="message-item">
                <div className="message-header">
                  <span className="message-time">
                    {new Date(msg.parsed.timestamp).toLocaleTimeString()}
                  </span>
                  <span className={`message-type type-${msg.parsed.type}`}>
                    {msg.parsed.type}
                  </span>
                  <span className="message-report-id">
                    Report: {msg.reportId}
                  </span>
                </div>
                <div className="message-data">
                  <div className="message-hex">
                    {messageHandler?.toHexString(msg.raw)}
                  </div>
                  {msg.parsed.data && (
                    <div className="message-parsed">
                      {JSON.stringify(msg.parsed.data, null, 2)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {filteredMessages.length === 0 && (
              <div className="no-messages">暂无消息</div>
            )}
          </div>
        </>
      ) : (
        <div className="debugger-device-data">
          {renderDeviceConfigFull()}
          {renderInformationDataRaw()}
          {renderMouseButtonData()}
          {renderPerformanceData()}
          {renderColorData()}
          {renderMacroData()}
        </div>
      )}
    </div>
  );
};

export default DeviceDebugger;
